/**
 * Formatea un documento legal (`content.ts`) para volcarlo en el log
 * del terminal CRT.
 *
 * El `<pre.dos>` del terminal usa `white-space: pre-wrap`, así que los
 * párrafos largos se ajustan solos al ancho de la pantalla — no hace
 * falta cortar líneas a mano. Sí normalizamos la tipografía fina a
 * ASCII (comillas angulares, rayas, puntos medios…) porque las fuentes
 * de fósforo (Perfect DOS VGA 437, Amstrad CPC464) no garantizan esos
 * glifos. Los acentos sí se conservan: los gestiona `font-substitutions`.
 */

import {
  LEGAL_LAST_UPDATED,
  type LegalBlock,
  type LegalDocument,
} from './content.js';

/**
 * Línea lista para `pushLine`. El `level` es un subconjunto de
 * `LogLevel` (state.ts) — todos válidos como argumento de `pushLine`.
 */
export interface CrtLine {
  text: string;
  level: 'normal' | 'title' | 'info';
}

/** Sustituye la tipografía fina por ASCII que el fósforo sí dibuja. */
function asciify(s: string): string {
  return s
    .replace(/[«»“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[—–]/g, '-')
    .replace(/·/g, '-')
    .replace(/…/g, '...');
}

/** Convierte un bloque (párrafo o lista) en líneas de terminal. */
function blockLines(block: LegalBlock): CrtLine[] {
  if (block.type === 'p') {
    return [{ text: asciify(block.text), level: 'normal' }];
  }
  return block.items.map((item) => ({
    text: `  - ${asciify(item)}`,
    level: 'normal' as const,
  }));
}

/**
 * Convierte un documento legal en la secuencia de líneas que el CRT
 * vuelca en su log al abrir el apartado correspondiente de ACERCA DE.
 */
export function formatLegalDocForCrt(doc: LegalDocument): CrtLine[] {
  const lines: CrtLine[] = [];

  lines.push({ text: '', level: 'normal' });
  lines.push({ text: asciify(doc.title), level: 'title' });
  lines.push({
    text: `Última actualización: ${asciify(LEGAL_LAST_UPDATED)}`,
    level: 'info',
  });
  lines.push({ text: '', level: 'normal' });

  for (const block of doc.intro) {
    lines.push(...blockLines(block));
  }

  doc.sections.forEach((section, i) => {
    lines.push({ text: '', level: 'normal' });
    lines.push({
      text: `${i + 1}. ${asciify(section.title)}`,
      level: 'title',
    });
    for (const block of section.body) {
      lines.push(...blockLines(block));
    }
  });

  lines.push({ text: '', level: 'normal' });
  lines.push({ text: '--- PULSA [ESC] PARA VOLVER ---', level: 'info' });

  return lines;
}
